import React from "react"
import { IFooterProps } from "./util"
import { ContactForm } from "../Forms"
import { ESocialChannel } from "../../lib"
import { FaFacebookF, FaInstagram, FaLinkedinIn, FaPinterest, FaTiktok, FaTwitter, FaVimeo, FaYoutube } from "react-icons/fa"
import { BsToggles } from "react-icons/bs"
import SegmentConsentManager from "../Utility/SegmentConsentManager"
import { openConsentManager } from "@segment/consent-manager"

export const FooterLabeledColumns: React.FC<IFooterProps> = ({
    className = "",
    offices,
    socialMediaLinks,
    linksGroups,
    colsTitles,
    htmlCols,
    copyright,
    utilsLinks,
    linkFunc,
    formProps,
    segmentWriteKey,
    segmentConsentManagerProps,
    showAttribution,
    children,
}) => {
    const LinkF = linkFunc || ((props) => <a {...props} />);
    return (
        <footer className={`relative flex flex-col items-center w-full ${className}`}>
            {children}
            <div className="flex flex-col items-center w-full max-w-screen-xl">
                <div className="flex flex-row flex-wrap justify-center w-full gap-y-6 py-14 px-4">
                    <div className="w-full lg:w-1/3 max-w-md px-4 lg:pr-7 lg:pl-0">
                        {colsTitles?.[0] && <h3 className="text-3xl w-full border-b mb-9 pb-3">{colsTitles[0]}</h3>}
                        <ContactForm {...formProps} />
                    </div>
                    <div className="w-full lg:w-1/3 max-w-md px-4 lg:px-7">
                        {colsTitles?.[1] && <h3 className="text-3xl w-full border-b mb-9 pb-3">{colsTitles[1]}</h3>}
                        {htmlCols?.[0] && htmlCols[0].trim().length > 0 && (
                            <div className="text-sm mb-4" dangerouslySetInnerHTML={{ __html: htmlCols[0] }}></div>
                        )}
                        {(offices?.[0] || socialMediaLinks?.[0]) && (
                            <dl className="flex flex-col">
                                {offices?.[0] && (
                                    <>
                                        {offices[0].phone && (
                                            <>
                                                <dt className="text-sm">Office</dt>
                                                <dd className="text-lg mb-4">{offices[0].phone}</dd>
                                            </>
                                        )}
                                        {offices[0].email && (
                                            <>
                                                <dt className="text-sm">Email</dt>
                                                <dd className="text-lg mb-4">{offices[0].email}</dd>
                                            </>
                                        )}
                                        {offices[0].address && (
                                            <>
                                                <dt className="text-sm">Address</dt>
                                                <dd className="text-lg mb-4">{offices[0].address.text}
                                                    {!offices[0].address.text && (
                                                        <>
                                                            {`${offices[0].address.street1} ${offices[0].address.street2 ?? ""}`}<br />
                                                            {`${offices[0].address.city} ${offices[0].address.state} ${offices[0].address.postal}`}
                                                        </>
                                                    )}
                                                </dd>
                                            </>
                                        )}
                                    </>
                                )}
                                {socialMediaLinks?.[0] && (
                                    <>
                                        <dt className="text-sm">Social</dt>
                                        <dd className="text-lg flex flex-row flex-wrap gap-2 w-full">
                                            {socialMediaLinks.map((item, i) => (
                                                <a
                                                    key={i}
                                                    href={`${item.url}`}
                                                    title={item.text}
                                                    target={item.target}
                                                    className="text-lg mt-1"
                                                >
                                                    {(() => {
                                                        switch (item.channel) {
                                                            case ESocialChannel.Facebook:
                                                                return <FaFacebookF />;
                                                            case ESocialChannel.Twitter:
                                                                return <FaTwitter />;
                                                            case ESocialChannel.Instagram:
                                                                return <FaInstagram />;
                                                            case ESocialChannel.YouTube:
                                                                return <FaYoutube />;
                                                            case ESocialChannel.LinkedIn:
                                                                return <FaLinkedinIn />;
                                                            case ESocialChannel.Vimeo:
                                                                return <FaVimeo />;
                                                            case ESocialChannel.Pinterest:
                                                                return <FaPinterest />;
                                                            case ESocialChannel.TikTok:
                                                                return <FaTiktok />;
                                                            default:
                                                                return null;
                                                        }
                                                    })()}
                                                </a>
                                            ))}
                                        </dd>
                                    </>
                                )}
                            </dl>
                        )}
                    </div>
                    <div className="w-full lg:w-1/3 max-w-md px-4 lg:pl-7 lg:pr-0">
                        {colsTitles?.[2] && <h3 className="text-3xl w-full border-b mb-9 pb-3">{colsTitles[2]}</h3>}
                        {htmlCols?.[1] && htmlCols[1].trim().length > 0 && (
                            <div className="text-sm mb-4" dangerouslySetInnerHTML={{ __html: htmlCols[1] }}></div>
                        )}
                        {linksGroups?.[0] && (
                            <div className="flex flex-row flex-wrap justify-between gap-x-4 lg:mr-14">
                                {linksGroups.map((group, index) => (
                                    <dl key={index}>
                                        <dt className="text-sm">{group.title}</dt>
                                        {group.links?.[0] && group.links.map((linkItem, i) => (
                                            <dd className="text-sm mb-3" key={i}>
                                                <LinkF
                                                    key={i}
                                                    href={`${linkItem.url}`}
                                                    target={linkItem.target}
                                                >
                                                    {linkItem.text}
                                                </LinkF>
                                            </dd>
                                        ))}
                                    </dl>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
                <div className="flex flex-row flex-wrap justify-between w-full gap-y-6 py-9 px-4 border-t">
                    {copyright && copyright.length > 0 && (
                        <div className="text-xs" dangerouslySetInnerHTML={{ __html: copyright }}></div>
                    )}
                    {utilsLinks?.[0] && (
                        <div className="flex flex-row flex-wrap gap-4">
                            {utilsLinks.map((linkItem, i) => (
                                <LinkF
                                    key={i}
                                    href={`${linkItem.url}`}
                                    target={linkItem.target}
                                    className="text-xs font-bold"
                                >
                                    {linkItem.text}
                                </LinkF>
                            ))}
                        </div>
                    )}
                    {segmentWriteKey && segmentWriteKey.length > 0 && (
                        <a className="cursor-pointer text-xs flex gap-x-1" onClick={openConsentManager}>
                            <BsToggles className="text-body-text" /> Website Data Collection Preferences
                        </a>
                    )}
                </div>
            </div>
            {segmentWriteKey && segmentWriteKey.length > 0 && (
                <SegmentConsentManager segmentWriteKey={segmentWriteKey} {...segmentConsentManagerProps} />
            )}
            {showAttribution && (
                <div className="attribution text-xs text-center pb-7">
                    Real estate website design, development and optimization by{" "}
                    <a href="https://www.organicreturn.com/" className="font-bold">
                        Organic Return
                    </a>
                </div>
            )}
        </footer>
    )
}

export default FooterLabeledColumns